/**
 * The OTP form itself: email step, then code step.
 *
 * Authority: bead scope-r3x5. Sibling of `CustomerAuthProvider.tsx`, which
 * owns the reducer and the session probe — this file only renders the two
 * steps and turns a submit into a dispatched action, the same split
 * `QuestionRenderer.tsx` documents for its own controls.
 *
 * Blueprint markup (card, mono label, 6-box code step, resend countdown):
 * bead scope-ovet.9, styles in `./sign-in.css`.
 */
import { useEffect, useState, type FormEvent } from 'react'

import { FirstContactNotice } from '../shell/FirstContactNotice.tsx'
import { italicLast } from '../shell/italicLast.tsx'
import { t } from '../shell/i18n.ts'
import { OtpRequestFailed, requestOtpCode, verifyOtpCode } from './customer-otp.ts'
import type { CustomerSignInAction, CustomerSignInState } from './customerSignInState.ts'
import './sign-in.css'

const CODE_LENGTH = 6

export interface CustomerSignInFormProps {
  readonly state: CustomerSignInState
  readonly dispatch: (action: CustomerSignInAction) => void
}

function errorMessage(error: unknown): string {
  if (!(error instanceof OtpRequestFailed)) return t('signIn.customer.error.generic')
  if (error.code === 'RATE_LIMITED') return t('signIn.customer.error.rateLimited')
  if (error.code === 'OTP_LOCKED') return t('signIn.customer.error.locked')
  if (error.code === 'OTP_INVALID' || error.code === 'OTP_EXPIRED') {
    return t('signIn.customer.error.codeInvalid')
  }

  return t('signIn.customer.error.generic')
}

/** `93` -> `"1:33"`. The resend affordance's mono countdown (scope-ovet §5). */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * Ticks `seconds` down to zero, resetting whenever `seconds` itself changes
 * (a fresh `CODE_REQUESTED` sets a new `resendAfterSeconds`). The resend
 * affordance reads 0 as "show the link", >0 as "show the countdown".
 */
function useResendCountdown(seconds: number): number {
  const [remaining, setRemaining] = useState(seconds)

  useEffect(() => {
    setRemaining(seconds)
    if (seconds <= 0) return

    const timer = setInterval(() => {
      setRemaining((current) => Math.max(0, current - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [seconds])

  return remaining
}

async function requestCode(email: string, dispatch: CustomerSignInFormProps['dispatch']) {
  dispatch({ type: 'CODE_REQUEST_STARTED' })
  try {
    const response = await requestOtpCode(email)
    dispatch({ type: 'CODE_REQUESTED', email, resendAfterSeconds: response.retry_after_seconds })
  } catch (error) {
    dispatch({ type: 'CODE_REQUEST_FAILED', message: errorMessage(error) })
  }
}

async function verifyCode(
  email: string,
  code: string,
  dispatch: CustomerSignInFormProps['dispatch'],
) {
  dispatch({ type: 'CODE_VERIFY_STARTED' })
  try {
    const verified = await verifyOtpCode(email, code)
    /*
     * IMPL-ENGAGE-002 §8.2. The response shape does not change between the two
     * cases — same fields, same status — so this flag is the only thing that
     * distinguishes them, and it is only meaningful after verification, which
     * is the one point where §2.1 permits the disclosure.
     */
    dispatch({ type: 'CODE_VERIFIED', selectionRequired: verified.selection_required })
  } catch (error) {
    dispatch({ type: 'CODE_VERIFY_FAILED', message: errorMessage(error) })
  }
}

export function CustomerSignInForm({ state, dispatch }: CustomerSignInFormProps) {
  if (state.step === 'checking') return <p role="status">{t('loading')}</p>
  // First contact for a returning customer is this form, not the entry
  // journey, so the S1 notice renders here once (redesign spec §2.1) —
  // above the email step only, not repeated on the code step.
  if (state.step === 'email')
    return (
      <>
        <FirstContactNotice />
        <EmailStep state={state} dispatch={dispatch} />
      </>
    )
  if (state.step === 'code') return <CodeStep state={state} dispatch={dispatch} />

  return null
}

/** The card's full-width primary; a const so the call sites stay one line. */
const CTA = 'npro-btn npro-btn--primary sign-in-card__button'

function EmailStep({
  state,
  dispatch,
}: {
  readonly state: Extract<CustomerSignInState, { step: 'email' }>
  readonly dispatch: CustomerSignInFormProps['dispatch']
}) {
  const [email, setEmail] = useState('')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    void requestCode(email, dispatch)
  }

  return (
    <div className="sign-in">
      <article className="sign-in-card">
        <p className="npro-eyebrow" aria-hidden="true">
          {t('signIn.customer.accessLabel')}
        </p>
        <h1 className="sign-in-card__heading">{italicLast(t('signIn.customer.heading'))}</h1>
        <p className="sign-in-card__body">{t('signIn.customer.body')}</p>
        <form onSubmit={submit} className="sign-in-card__form">
          <label htmlFor="customer-sign-in-email" className="sign-in-card__field-label">
            {t('signIn.customer.emailLabel')}
          </label>
          <input
            id="customer-sign-in-email"
            className="sign-in-card__input"
            type="email"
            autoComplete="email"
            required
            value={email}
            disabled={state.pending}
            onChange={(event) => setEmail(event.target.value)}
          />
          {state.error !== null && (
            <p role="alert" className="sign-in-card__error">
              {state.error}
            </p>
          )}
          <button type="submit" className={CTA} disabled={state.pending}>
            {t('signIn.customer.sendCode')}
          </button>
        </form>
      </article>
    </div>
  )
}

/*
 * Mock 1D marks the digits you have TYPED with the ink keyline, and shows the
 * next empty cell as a stone dot. The previous reading was inverted — the ink
 * border tracked the caret and every typed digit sat on the stone rule, so
 * progress through the code was the one thing the row did not show.
 */
function CodeBoxes({ code }: { readonly code: string }) {
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => code[index] ?? '')

  return (
    <div className="code-step__boxes" aria-hidden="true">
      {digits.map((digit, index) => (
        <span
          key={index}
          className="code-step__box"
          data-filled={digit === '' ? undefined : 'true'}
          data-next={index === code.length ? 'true' : undefined}
        >
          {digit}
        </span>
      ))}
    </div>
  )
}

function ResendAffordance({
  state,
  dispatch,
}: {
  readonly state: Extract<CustomerSignInState, { step: 'code' }>
  readonly dispatch: CustomerSignInFormProps['dispatch']
}) {
  const secondsLeft = useResendCountdown(state.resendAfterSeconds)
  if (secondsLeft > 0) {
    return (
      <p className="code-step__resend-countdown" aria-live="polite">
        {t('signIn.customer.resendCountdown', { time: formatCountdown(secondsLeft) })}
      </p>
    )
  }

  return (
    <button
      type="button"
      className="code-step__resend-link"
      disabled={state.pending}
      onClick={() => void requestCode(state.email, dispatch)}
    >
      {t('signIn.customer.resend')}
    </button>
  )
}

function CodeForm({
  state,
  code,
  setCode,
  dispatch,
}: {
  readonly state: Extract<CustomerSignInState, { step: 'code' }>
  readonly code: string
  readonly setCode: (code: string) => void
  readonly dispatch: CustomerSignInFormProps['dispatch']
}) {
  const submit = (event: FormEvent) => {
    event.preventDefault()
    void verifyCode(state.email, code, dispatch)
  }

  return (
    <form onSubmit={submit} className="sign-in-card__form">
      <label htmlFor="customer-sign-in-code" className="sign-in-card__field-label">
        {t('signIn.customer.codeLabel')}
      </label>
      <div className="code-step">
        <input
          id="customer-sign-in-code"
          className="code-step__input"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={CODE_LENGTH}
          autoComplete="one-time-code"
          required
          value={code}
          disabled={state.pending}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
        />
        <CodeBoxes code={code} />
      </div>
      {state.error !== null && (
        <p role="alert" className="sign-in-card__error">
          {state.error}
        </p>
      )}
      <button type="submit" className={CTA} disabled={state.pending}>
        {t('signIn.customer.verifyCode')}
      </button>
    </form>
  )
}

function CodeStep({
  state,
  dispatch,
}: {
  readonly state: Extract<CustomerSignInState, { step: 'code' }>
  readonly dispatch: CustomerSignInFormProps['dispatch']
}) {
  const [code, setCode] = useState('')

  return (
    <div className="sign-in">
      <article className="sign-in-card">
        <p className="npro-eyebrow" aria-hidden="true">
          {t('signIn.customer.accessLabel')}
        </p>
        <h1 className="sign-in-card__heading">{italicLast(t('signIn.customer.heading'))}</h1>
        <p className="sign-in-card__body">
          {t('signIn.customer.codeBody', { email: state.email })}
        </p>
        <CodeForm state={state} code={code} setCode={setCode} dispatch={dispatch} />
        <ResendAffordance state={state} dispatch={dispatch} />
      </article>
    </div>
  )
}
